import React from "react"
import { useNavigate, useParams } from "react-router-dom"
import _ from "lodash"
import { Button } from "@mui/material"
import {
    ArrowBack,
    ScheduleOutlined,
    CalendarMonthOutlined,
    PlayCircleOutline,
    LockOutlined,
    CheckCircle,
    ForumOutlined,
    AttachFileOutlined,
    VisibilityOutlined
} from "@mui/icons-material"
import { Loader } from "../../components/index"
import {
    useGetLectureByIdQuery,
    useGetCourseDetailQuery
} from "../../Redux/actions/lecture.action"
import { learnPaths } from "../constants"
import VideoPlayer from "../components/VideoPlayer"
import EmptyState from "../components/EmptyState"
import ResourceList from "../components/ResourceList"
import LectureQA from "../components/LectureQA"
import { groupLecturesByChapter, isLectureLocked } from "../chapters"

/**
 * Watch page. On large screens the rest of the course sits beside the player
 * as a playlist grouped by chapter; on small screens it stacks underneath.
 *
 * Enrolled students can watch every lecture and join its Q&A. A student who
 * has not bought the course can still watch the free preview lectures, but
 * not the rest, and is invited to enrol to take part in the discussion.
 */
export default function LecturePlayerPage() {
    const { course_id, lecture_id } = useParams()
    const navigate = useNavigate()

    /*
     * Access is confirmed before the video is even requested. The server
     * refuses a locked lecture anyway; this keeps the page from ever
     * rendering a player for one.
     */
    const courseQuery = useGetCourseDetailQuery({ course_id })
    const enrolled = Boolean(_.get(courseQuery, "data.is_enrolled", false))

    const course = _.get(courseQuery, "data.course", null)
    const playlist = React.useMemo(
        () => _.get(courseQuery, "data.lectures", []),
        [courseQuery]
    )
    const chapters = React.useMemo(
        () => _.get(courseQuery, "data.chapters", null) || [],
        [courseQuery]
    )
    const sections = React.useMemo(
        () => groupLecturesByChapter(chapters, playlist),
        [chapters, playlist]
    )

    const current = playlist.find(
        (item) => String(item.lecture_id) === String(lecture_id)
    )
    const canWatch = enrolled || Boolean(current && !isLectureLocked(current, false))

    const lectureQuery = useGetLectureByIdQuery(
        { lecture_id },
        { skip: !courseQuery.isSuccess || !canWatch }
    )
    const lecture = _.get(lectureQuery, "data.lecture", null)
    const resources = _.get(lecture, "resources", null) || []

    /* Scroll back to the top whenever the student picks another lecture. */
    React.useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" })
    }, [lecture_id])

    if (courseQuery.isSuccess && !canWatch) {
        return (
            <EmptyState
                icon={<LockOutlined />}
                title="Buy this course to watch"
                description="This lecture unlocks as soon as you buy the course. Lectures marked Preview can be watched for free."
                actionLabel="View the course"
                onAction={() => navigate(learnPaths.course(course_id))}
            />
        )
    }

    if (courseQuery.isError || lectureQuery.isError) {
        const failed = courseQuery.isError ? courseQuery : lectureQuery
        return (
            <EmptyState
                icon={<PlayCircleOutline />}
                title="Lecture unavailable"
                description={
                    _.get(failed, "error.data.detail", null) ||
                    "This lecture could not be loaded."
                }
                actionLabel="Back to the course"
                onAction={() => navigate(learnPaths.course(course_id))}
            />
        )
    }

    /* Lectures are numbered straight through the course, across chapters. */
    let position = 0

    return (
        <div className="space-y-5">

            <Button
                size="small"
                startIcon={<ArrowBack />}
                onClick={() => navigate(learnPaths.course(course_id))}
            >
                Back to {course?.title || "the course"}
            </Button>

            {/* Watching as a preview */}
            {courseQuery.isSuccess && !enrolled && (
                <div className="flex flex-col gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 sm:flex-row sm:items-center sm:justify-between">
                    <p className="flex items-start gap-2 text-sm text-emerald-800">
                        <VisibilityOutlined sx={{ fontSize: 18, mt: "1px" }} />
                        You are watching a free preview. Buy the course to
                        unlock all {playlist.length} lectures.
                    </p>
                    <Button
                        size="small"
                        variant="contained"
                        color="success"
                        onClick={() => navigate(learnPaths.course(course_id))}
                    >
                        View the course
                    </Button>
                </div>
            )}

            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">

                {/* Player + details */}
                <div className="min-w-0 space-y-4 lg:col-span-2">
                    <VideoPlayer
                        url={lecture?.video_url}
                        title={lecture?.title}
                    />

                    <div className="rounded-2xl border border-slate-200 bg-white p-5">
                        <h1 className="text-lg font-bold text-slate-800 sm:text-xl">
                            {lecture?.title || "Loading..."}
                        </h1>

                        <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-500 sm:text-sm">
                            {lecture?.subject_name && (
                                <span className="rounded-full bg-indigo-50 px-3 py-1 font-medium text-indigo-600">
                                    {lecture.subject_name}
                                </span>
                            )}
                            {lecture?.duration && (
                                <span className="flex items-center gap-1">
                                    <ScheduleOutlined sx={{ fontSize: 16 }} />
                                    {lecture.duration}
                                </span>
                            )}
                            {lecture?.created_at && (
                                <span className="flex items-center gap-1">
                                    <CalendarMonthOutlined sx={{ fontSize: 16 }} />
                                    {lecture.created_at}
                                </span>
                            )}
                        </div>

                        {lecture?.description && (
                            <p className="mt-4 text-sm leading-relaxed text-slate-600">
                                {lecture.description}
                            </p>
                        )}

                        {/* Resources */}
                        {resources.length > 0 && (
                            <div className="mt-5 border-t border-slate-100 pt-4">
                                <h2 className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-700">
                                    <AttachFileOutlined sx={{ fontSize: 18 }} />
                                    Resources
                                </h2>
                                <ResourceList resources={resources} />
                            </div>
                        )}
                    </div>

                    {/* Q&A -- enrolled students only */}
                    {enrolled && lecture && <LectureQA lecture_id={lecture_id} />}

                    {courseQuery.isSuccess && !enrolled && (
                        <section className="flex flex-col items-start gap-3 rounded-2xl border border-dashed border-slate-300 bg-white p-5 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <h2 className="flex items-center gap-2 text-base font-semibold text-slate-800">
                                    <ForumOutlined sx={{ fontSize: 20 }} />
                                    Questions & answers
                                </h2>
                                <p className="mt-1 text-sm text-slate-500">
                                    Enrol in this course to ask questions and
                                    join the discussion with the instructor.
                                </p>
                            </div>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={() => navigate(learnPaths.course(course_id))}
                            >
                                Enrol to join
                            </Button>
                        </section>
                    )}
                </div>

                {/* Playlist */}
                <aside className="space-y-3">
                    <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                        More in this course
                    </h2>

                    <div className="space-y-4 lg:max-h-[70vh] lg:overflow-y-auto lg:pr-1">
                        {sections.map(({ chapter, lectures: items }) => (
                            <div
                                key={chapter ? chapter.chapter_id : "ungrouped"}
                                className="space-y-2"
                            >
                                {chapters.length > 0 && (
                                    <h3 className="flex items-baseline justify-between gap-2 px-1 text-xs font-semibold text-slate-700">
                                        <span className="truncate">
                                            {chapter ? chapter.title : "More lectures"}
                                        </span>
                                        <span className="shrink-0 font-normal text-slate-400">
                                            {items.length}
                                        </span>
                                    </h3>
                                )}

                                {items.map((item) => {
                                    position += 1
                                    const isCurrent =
                                        String(item.lecture_id) === String(lecture_id)
                                    const locked = isLectureLocked(item, enrolled)
                                    const done = Boolean(item.progress?.completed)
                                    return (
                                        <button
                                            type="button"
                                            key={item.lecture_id}
                                            onClick={() =>
                                                navigate(
                                                    locked
                                                        ? learnPaths.course(course_id)
                                                        : learnPaths.lecture(
                                                            course_id,
                                                            item.lecture_id
                                                        )
                                                )
                                            }
                                            className={`flex w-full items-center gap-3 rounded-xl border p-3 text-left transition ${
                                                isCurrent
                                                    ? "border-indigo-400 bg-indigo-50/60"
                                                    : "border-slate-200 bg-white hover:border-indigo-300"
                                            } ${locked ? "opacity-70" : ""}`}
                                        >
                                            <span
                                                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-bold ${
                                                    isCurrent
                                                        ? "bg-indigo-600 text-white"
                                                        : "bg-slate-100 text-slate-500"
                                                }`}
                                            >
                                                {locked ? (
                                                    <LockOutlined sx={{ fontSize: 16 }} />
                                                ) : done ? (
                                                    <CheckCircle
                                                        sx={{
                                                            fontSize: 18,
                                                            color: isCurrent ? "#fff" : "#059669"
                                                        }}
                                                    />
                                                ) : (
                                                    position
                                                )}
                                            </span>
                                            <span className="min-w-0 flex-1">
                                                <span className="block truncate text-sm font-medium text-slate-800">
                                                    {item.title}
                                                </span>
                                                <span className="flex items-center gap-2 text-xs text-slate-400">
                                                    {item.duration && <span>{item.duration}</span>}
                                                    {!enrolled && item.is_preview && (
                                                        <span className="font-semibold text-emerald-600">
                                                            Preview
                                                        </span>
                                                    )}
                                                </span>
                                            </span>
                                        </button>
                                    )
                                })}
                            </div>
                        ))}
                    </div>
                </aside>
            </div>

            {(lectureQuery.isLoading || courseQuery.isLoading) && (
                <Loader show={true} message="Loading lecture..." />
            )}
        </div>
    )
}
